import React, { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, BellOff, Plus, X, Send, Smile, SmilePlus, Paperclip, FileText, Download, Pin, PinOff, Loader2, Check, CheckCheck, Reply, Search, ChevronUp, ChevronDown, Star, Mic, Trash2, BarChart3 } from "lucide-react";
import { useAuth } from "../AuthContext";
import { useSpecialty } from "../SpecialtyContext";
import { API_URL as REST_URL, WS_URL } from "../config";
import { isPushSupported, getCurrentSubscription, subscribeToPush, unsubscribeFromPush } from "../pushNotifications";
import EmojiPicker from "./EmojiPicker";
import VoicePlayer from "./VoicePlayer";
import ChatContentPreview, { extractContentLink } from "./ChatContentPreview";
import PollView from "./PollView";
import PollComposer from "./PollComposer";

const MAX_LEN = 2000;
const lastSeenKey = id => `chat_last_seen_${id}`;
const MAX_RECONNECT_DELAY = 15000;
const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "😢", "🙏"];

/** Форматировать ISO-строку или объект Date в HH:MM */
function fmtTime(ts) {
  if (!ts) return "";
  try {
    return new Date(ts).toLocaleTimeString("ru", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function fmtFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

export default function Forum({ onLoginRequest }) {
  const { token, isLoggedIn, user, isEditor, isAdmin } = useAuth();
  const { specialty } = useSpecialty();
  const location = useLocation();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [pendingImage, setPendingImage] = useState(null); // { url, previewUrl }
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // { url, name, size }
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);
  const fileDocInputRef = useRef(null);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [showPollComposer, setShowPollComposer] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const textInputRef = useRef(null);
  const [wsStatus, setWsStatus] = useState("connecting"); // connecting | open | closed | readonly (гость)
  const [unread, setUnread] = useState({});
  const chatRef = useRef(null);
  const wsRef = useRef(null);
  const myMsgIds = useRef(new Set());
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const [typingUsers, setTypingUsers] = useState([]);
  const typingTimeoutsRef = useRef({});
  const lastTypingSentRef = useRef(0);
  const [readReceipts, setReadReceipts] = useState({});
  const lastReadSentRef = useRef(0);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchIndex, setSearchIndex] = useState(0);
  const msgRefs = useRef({});
  const [reactingToId, setReactingToId] = useState(null);

  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [createError, setCreateError] = useState(null);
  const [createBusy, setCreateBusy] = useState(false);
  const [myChannelIds, setMyChannelIds] = useState(new Set());
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [followBusyId, setFollowBusyId] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(() => !localStorage.getItem("forum_onboarding_dismissed"));
  const dismissOnboarding = () => { localStorage.setItem("forum_onboarding_dismissed", "1"); setShowOnboarding(false); };

  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingCancelledRef = useRef(false);

  // Редактор может быть закреплён за несколькими специальностями сразу (хранятся через ";")
  const userSpecialties = (user?.specialty || "").split(";").map(s => s.trim()).filter(Boolean);

  // Может ли текущий пользователь создавать каналы в текущей специальности
  const canCreateChannel = isAdmin || (isEditor && userSpecialties.includes(specialty?.id));

  // Загрузка списка каналов для текущей специальности
  const loadChannels = useCallback(() => {
    fetch(`${REST_URL}/channels?specialty=${specialty?.id || ""}`)
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        setChannels(list);
        setActiveChannel(prev => {
          if (prev && list.some(c => c.slug === prev)) return prev;
          return list[0]?.slug || null;
        });
      })
      .catch(() => setChannels([]));
  }, [specialty]);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  // Кнопка «Обсудить» на статье/случае/материале переносит сюда готовую ссылку
  useEffect(() => {
    if (location.state?.prefillText) {
      setText(location.state.prefillText);
      navigate(location.pathname, { replace: true, state: null });
      setTimeout(() => textInputRef.current?.focus(), 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Личная подписка на каналы (персонализация, независимо от специальности) —
  // чтобы врач одной специальности не тонул в уведомлениях из всех остальных
  const loadMyChannelIds = useCallback(() => {
    if (!token) { setMyChannelIds(new Set()); return; }
    fetch(`${REST_URL}/channels/mine`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(list => setMyChannelIds(new Set(list.map(c => c.id))))
      .catch(() => {});
  }, [token]);

  useEffect(() => { loadMyChannelIds(); }, [loadMyChannelIds]);

  const toggleFollowChannel = async (channel, e) => {
    e.stopPropagation();
    if (!token) { onLoginRequest?.(); return; }
    setFollowBusyId(channel.id);
    const isFollowing = myChannelIds.has(channel.id);
    try {
      await fetch(`${REST_URL}/channels/${channel.id}/follow`, {
        method: isFollowing ? "DELETE" : "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      await loadMyChannelIds();
    } finally {
      setFollowBusyId(null);
    }
  };

  const visibleChannels = showOnlyMine ? channels.filter(c => myChannelIds.has(c.id)) : channels;

  const createChannel = async (e) => {
    e.preventDefault();
    if (!newLabel.trim()) return;
    setCreateBusy(true);
    setCreateError(null);
    try {
      const r = await fetch(`${REST_URL}/channels`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ label: newLabel.trim(), description: newDesc.trim() || null, specialty: specialty?.id || null }),
      });
      const data = await r.json();
      if (r.ok) {
        setNewLabel(""); setNewDesc(""); setShowCreate(false);
        loadChannels();
        setActiveChannel(data.slug);
      } else {
        setCreateError(data.detail || "Не удалось создать канал");
      }
    } catch {
      setCreateError("Ошибка сети");
    } finally {
      setCreateBusy(false);
    }
  };

  const deleteChannel = (id) => {
    if (!window.confirm("Удалить канал вместе с историей сообщений?")) return;
    fetch(`${REST_URL}/channels/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.ok) loadChannels(); })
      .catch(() => {});
  };

  // Автоскролл вниз при новых сообщениях
  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  // Проверка текущего статуса push-подписки
  useEffect(() => {
    if (!isLoggedIn || !isPushSupported()) return;
    getCurrentSubscription().then(sub => setPushEnabled(!!sub)).catch(() => {});
  }, [isLoggedIn]);

  const togglePush = async () => {
    setPushBusy(true);
    setPushError(null);
    try {
      if (pushEnabled) {
        await unsubscribeFromPush(token);
        setPushEnabled(false);
      } else {
        await subscribeToPush(token);
        setPushEnabled(true);
      }
    } catch (err) {
      setPushError(err.message);
    } finally {
      setPushBusy(false);
    }
  };

  // При смене канала: закрыть WS, загрузить историю, открыть новый WS (с автореконнектом)
  const connectChannel = useCallback((channelId) => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    reconnectAttemptRef.current = 0;

    if (wsRef.current) {
      wsRef.current.onclose = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    setMessages([]);
    // Гость только читает историю: живое соединение требует входа, пытаться его открыть бессмысленно
    setWsStatus(token ? "connecting" : "readonly");

    fetch(`${REST_URL}/chat/messages?channel=${channelId}&limit=100`)
      .then(r => r.ok ? r.json() : [])
      .then(history => setMessages(history))
      .catch(() => setMessages([]));

    fetch(`${REST_URL}/chat/messages/pinned?channel=${channelId}`)
      .then(r => r.ok ? r.json() : [])
      .then(setPinnedMessages)
      .catch(() => setPinnedMessages([]));

    const openSocket = () => {
      const wsUrl = token
        ? `${WS_URL}/ws/${channelId}?token=${token}`
        : `${WS_URL}/ws/${channelId}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsStatus("open");
        reconnectAttemptRef.current = 0;
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === "delete") {
            setMessages(prev => prev.filter(m => m.id !== data.id));
            setPinnedMessages(prev => prev.filter(m => m.id !== data.id));
            return;
          }
          if (data.type === "pin") {
            setMessages(prev => prev.map(m => m.id === data.id ? { ...m, is_pinned: data.is_pinned } : m));
            if (data.is_pinned) {
              setMessages(prev => {
                const msg = prev.find(m => m.id === data.id);
                if (msg) {
                  setPinnedMessages(pp => pp.some(p => p.id === data.id) ? pp : [{ ...msg, is_pinned: true }, ...pp]);
                }
                return prev;
              });
            } else {
              setPinnedMessages(prev => prev.filter(m => m.id !== data.id));
            }
            return;
          }
          if (data.type === "typing") {
            if (data.author === user?.display_name) return;
            setTypingUsers(prev => prev.includes(data.author) ? prev : [...prev, data.author]);
            clearTimeout(typingTimeoutsRef.current[data.author]);
            typingTimeoutsRef.current[data.author] = setTimeout(() => {
              setTypingUsers(prev => prev.filter(a => a !== data.author));
            }, 3000);
            return;
          }
          if (data.type === "read") {
            if (data.author === user?.display_name) return;
            setReadReceipts(prev => (prev[data.author] || 0) >= data.up_to_id
              ? prev : { ...prev, [data.author]: data.up_to_id });
            return;
          }
          if (data.type === "transcript") {
            setMessages(prev => prev.map(m => m.id === data.id ? { ...m, transcript: data.transcript } : m));
            return;
          }
          if (data.type === "mod_error") {
            setUploadError(data.message);
            setTimeout(() => setUploadError(null), 5000);
            return;
          }
          if (data.type === "reaction") {
            applyReactionEvent(data);
            return;
          }
          if (data.type === "poll_update") {
            setMessages(prev => prev.map(m => {
              if (m.poll?.id !== data.poll_id) return m;
              const mergedOptions = data.options.map(o => {
                const existing = m.poll.options.find(x => x.id === o.id);
                return { ...o, mine: existing?.mine || false };
              });
              return { ...m, poll: { ...m.poll, options: mergedOptions, total_votes: data.total_votes } };
            }));
            return;
          }
          setMessages(prev => {
            if (prev.some(m => m.id === data.id)) return prev;
            return [...prev, data];
          });
        } catch {}
      };

      ws.onerror = () => setWsStatus("closed");

      ws.onclose = (evt) => {
        setWsStatus("closed");
        wsRef.current = null;
        // Авто-реконнект, кроме намеренного закрытия (1000) и ошибки авторизации (4001)
        if (evt.code !== 1000 && evt.code !== 4001) {
          const attempt = reconnectAttemptRef.current + 1;
          reconnectAttemptRef.current = attempt;
          const delay = Math.min(1000 * 2 ** attempt, MAX_RECONNECT_DELAY);
          reconnectTimeoutRef.current = setTimeout(openSocket, delay);
        }
      };
    };

    if (token) openSocket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Подключение при монтировании и смене канала
  useEffect(() => {
    if (!activeChannel) return;
    connectChannel(activeChannel);
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close(1000);
      }
      Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
      typingTimeoutsRef.current = {};
      setTypingUsers([]);
      setReadReceipts({});
      lastReadSentRef.current = 0;
    };
  }, [activeChannel, connectChannel]);

  // Отправляем "прочитано" при получении новых сообщений (чат открыт = все видны)
  useEffect(() => {
    if (messages.length === 0 || wsStatus !== "open") return;
    const lastId = messages[messages.length - 1].id;
    if (typeof lastId !== "number" || lastId <= lastReadSentRef.current) return;
    lastReadSentRef.current = lastId;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "read", up_to_id: lastId }));
    }
  }, [messages, wsStatus]);

  const applyReactionEvent = ({ message_id, emoji, user_id, action }) => {
    setMessages(prev => prev.map(m => {
      if (m.id !== message_id) return m;
      const isMine = user_id === user?.id;
      const reactions = [...(m.reactions || [])];
      const idx = reactions.findIndex(r => r.emoji === emoji);
      if (action === "add") {
        if (idx >= 0) reactions[idx] = { ...reactions[idx], count: reactions[idx].count + 1, mine: reactions[idx].mine || isMine };
        else reactions.push({ emoji, count: 1, mine: isMine });
      } else if (idx >= 0) {
        const count = reactions[idx].count - 1;
        if (count <= 0) reactions.splice(idx, 1);
        else reactions[idx] = { ...reactions[idx], count, mine: isMine ? false : reactions[idx].mine };
      }
      return { ...m, reactions };
    }));
  };

  const toggleReaction = (messageId, emoji) => {
    setReactingToId(null);
    if (!isLoggedIn) { onLoginRequest?.(); return; }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "react", message_id: messageId, emoji }));
      return;
    }
    fetch(`${REST_URL}/chat/messages/${messageId}/react`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ emoji }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(reactions => { if (reactions) setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions } : m)); })
      .catch(() => {});
  };

  const notifyTyping = () => {
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "typing" }));
    }
  };

  // Непрочитанное по неактивным каналам — лёгкий поллинг
  const checkUnread = useCallback(() => {
    channels.forEach(c => {
      if (c.slug === activeChannel) return;
      fetch(`${REST_URL}/chat/messages?channel=${c.slug}&limit=1`)
        .then(r => r.ok ? r.json() : [])
        .then(arr => {
          if (!arr.length) return;
          const last = arr[arr.length - 1];
          const lastSeen = localStorage.getItem(lastSeenKey(c.slug));
          const isUnread = !lastSeen || new Date(last.created_at) > new Date(lastSeen);
          setUnread(prev => prev[c.slug] === isUnread ? prev : { ...prev, [c.slug]: isUnread });
        })
        .catch(() => {});
    });
  }, [activeChannel, channels]);

  useEffect(() => {
    checkUnread();
    const interval = setInterval(checkUnread, 25000);
    return () => clearInterval(interval);
  }, [checkUnread]);

  // Отметить текущий канал прочитанным при входе/выходе
  useEffect(() => {
    if (!activeChannel) return;
    localStorage.setItem(lastSeenKey(activeChannel), new Date().toISOString());
    setUnread(prev => prev[activeChannel] ? { ...prev, [activeChannel]: false } : prev);
    return () => {
      localStorage.setItem(lastSeenKey(activeChannel), new Date().toISOString());
    };
  }, [activeChannel]);

  const handleFileSelect = async e => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadError(null);
    setUploadingImage(true);
    const previewUrl = URL.createObjectURL(file);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await fetch(`${REST_URL}/upload/chat-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await r.json();
      if (r.ok && data.url) {
        setPendingImage({ url: data.url, previewUrl });
      } else {
        setUploadError(data.error || "Не удалось загрузить фото");
      }
    } catch {
      setUploadError("Ошибка сети при загрузке фото");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileDocSelect = async e => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadError(null);
    setUploadingFile(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await fetch(`${REST_URL}/upload/chat-file`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await r.json();
      if (r.ok && data.url) {
        setPendingFile({ url: data.url, name: data.name, size: data.size });
      } else {
        setUploadError(data.error || "Не удалось загрузить файл");
      }
    } catch {
      setUploadError("Ошибка сети при загрузке файла");
    } finally {
      setUploadingFile(false);
    }
  };

  const insertEmoji = emoji => {
    setText(prev => (prev + emoji).slice(0, MAX_LEN));
    textInputRef.current?.focus();
  };

  const handleTextChange = e => {
    const value = e.target.value.slice(0, MAX_LEN);
    setText(value);
    notifyTyping();

    const cursor = e.target.selectionStart;
    const match = value.slice(0, cursor).match(/@([a-zA-Zа-яА-Я0-9_]{1,20})$/);
    if (match && token) {
      fetch(`${REST_URL}/auth/users/search?q=${encodeURIComponent(match[1])}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : [])
        .then(setMentionSuggestions)
        .catch(() => setMentionSuggestions([]));
    } else {
      setMentionSuggestions([]);
    }
  };

  const selectMention = u => {
    const cursor = textInputRef.current?.selectionStart ?? text.length;
    const before = text.slice(0, cursor).replace(/@([a-zA-Zа-яА-Я0-9_]{0,20})$/, `@${u.username} `);
    const newText = (before + text.slice(cursor)).slice(0, MAX_LEN);
    setText(newText);
    setMentionSuggestions([]);
    textInputRef.current?.focus();
  };

  const send = e => {
    e.preventDefault();
    const msgText = text.trim().slice(0, MAX_LEN);
    const imageUrl = pendingImage?.url || null;
    const fileUrl = pendingFile?.url || null;
    const replyToId = replyingTo?.id || null;
    if (!msgText && !imageUrl && !fileUrl) return;

    const authHeaders = token ? { "Authorization": `Bearer ${token}` } : {};
    const payload = {
      author: user?.display_name, text: msgText, image_url: imageUrl, reply_to_id: replyToId,
      file_url: fileUrl, file_name: pendingFile?.name || null, file_size: pendingFile?.size || null,
    };

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(payload));
      myMsgIds.current.add("pending");
      setText("");
      setPendingImage(null);
      setPendingFile(null);
      setReplyingTo(null);
      setMentionSuggestions([]);
      return;
    }

    fetch(`${REST_URL}/chat/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders },
      body: JSON.stringify({
        text: msgText, channel: activeChannel, image_url: imageUrl, reply_to_id: replyToId,
        file_url: fileUrl, file_name: pendingFile?.name || null, file_size: pendingFile?.size || null,
      })
    })
      .then(async r => {
        const data = await r.json().catch(() => null);
        if (!r.ok) { setUploadError(data?.detail || "Не удалось отправить сообщение"); setTimeout(() => setUploadError(null), 5000); return; }
        if (data) { myMsgIds.current.add(data.id); setMessages(prev => [...prev, data]); }
      })
      .catch(() => {});
    setText("");
    setPendingImage(null);
    setPendingFile(null);
    setReplyingTo(null);
    setMentionSuggestions([]);
  };

  const uploadAndSendAudio = async blob => {
    setUploadingAudio(true);
    const authHeaders = token ? { "Authorization": `Bearer ${token}` } : {};
    const replyToId = replyingTo?.id || null;
    try {
      const form = new FormData();
      form.append("file", blob, `voice.${blob.type.includes("mp4") ? "m4a" : "webm"}`);
      const r = await fetch(`${REST_URL}/upload/chat-audio`, { method: "POST", headers: authHeaders, body: form });
      const data = await r.json();
      if (r.ok && data.url) {
        const payload = { author: user?.display_name, text: "", audio_url: data.url, reply_to_id: replyToId };
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify(payload));
        } else {
          fetch(`${REST_URL}/chat/messages`, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders },
            body: JSON.stringify({ text: "", channel: activeChannel, audio_url: data.url, reply_to_id: replyToId }),
          }).then(r2 => r2.ok ? r2.json() : null)
            .then(msg => { if (msg) { myMsgIds.current.add(msg.id); setMessages(prev => [...prev, msg]); } })
            .catch(() => {});
        }
        setReplyingTo(null);
      } else {
        setUploadError(data.error || "Не удалось отправить голосовое сообщение");
      }
    } catch {
      setUploadError("Ошибка сети при отправке голосового сообщения");
    } finally {
      setUploadingAudio(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      recordingCancelledRef.current = false;
      audioChunksRef.current = [];
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = e => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        clearInterval(recordingTimerRef.current);
        setIsRecording(false);
        if (!recordingCancelledRef.current && audioChunksRef.current.length > 0) {
          const blob = new Blob(audioChunksRef.current, { type: mimeType });
          uploadAndSendAudio(blob);
        }
        setRecordingSeconds(0);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => setRecordingSeconds(s => s + 1), 1000);
    } catch {
      setUploadError("Не удалось получить доступ к микрофону");
    }
  };

  const stopRecording = (cancel = false) => {
    recordingCancelledRef.current = cancel;
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
  };

  useEffect(() => {
    return () => {
      clearInterval(recordingTimerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        recordingCancelledRef.current = true;
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const searchResults = searchQuery.trim()
    ? messages.filter(m => (m.text || "").toLowerCase().includes(searchQuery.trim().toLowerCase()))
    : [];

  useEffect(() => { setSearchIndex(0); }, [searchQuery]);

  useEffect(() => {
    if (reactingToId === null) return;
    const close = () => setReactingToId(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [reactingToId]);

  useEffect(() => {
    if (searchResults.length === 0) return;
    const idx = Math.min(searchIndex, searchResults.length - 1);
    const msg = searchResults[searchResults.length - 1 - idx];
    const el = msgRefs.current[msg.id];
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchIndex, searchQuery, messages.length]);

  const renderMentions = text => {
    const re = /@([a-zA-Z0-9_]{3,20})/g;
    const parts = [];
    let lastIndex = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
      parts.push(<span key={m.index} style={{ color: "#007c84", fontWeight: 700 }}>@{m[1]}</span>);
      lastIndex = m.index + m[0].length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length > 1 ? <>{parts}</> : text;
  };

  const highlightText = text => {
    if (!searchQuery.trim()) return renderMentions(text);
    const q = searchQuery.trim();
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark style={{ background: "#ffc2d9", borderRadius: 2 }}>{text.slice(idx, idx + q.length)}</mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const deleteMessage = (id) => {
    if (!window.confirm("Удалить сообщение?")) return;
    fetch(`${REST_URL}/chat/messages/${id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => { if (r.ok) setMessages(prev => prev.filter(m => m.id !== id)); })
      .catch(() => {});
  };

  const togglePin = (msg) => {
    fetch(`${REST_URL}/chat/messages/${msg.id}/pin`, {
      method: msg.is_pinned ? "DELETE" : "POST",
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(updated => {
        if (!updated) return;
        setMessages(prev => prev.map(m => m.id === updated.id ? { ...m, is_pinned: updated.is_pinned } : m));
        setPinnedMessages(prev => updated.is_pinned
          ? (prev.some(p => p.id === updated.id) ? prev : [{ ...msg, is_pinned: true }, ...prev])
          : prev.filter(p => p.id !== updated.id));
      })
      .catch(() => {});
  };

  const createPoll = async ({ question, options }) => {
    if (!isLoggedIn) { onLoginRequest?.(); return; }
    const r = await fetch(`${REST_URL}/chat/polls`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ channel: activeChannel, question, options }),
    });
    const data = await r.json().catch(() => null);
    if (!r.ok) throw new Error(data?.detail || "Не удалось создать опрос");
    myMsgIds.current.add(data.id);
    setMessages(prev => prev.some(m => m.id === data.id) ? prev : [...prev, data]);
  };

  const voteOnPoll = (pollId, optionId) => {
    if (!isLoggedIn) { onLoginRequest?.(); return; }
    fetch(`${REST_URL}/chat/polls/${pollId}/vote`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ option_id: optionId }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(updatedPoll => {
        if (!updatedPoll) return;
        setMessages(prev => prev.map(m => m.poll?.id === pollId ? { ...m, poll: updatedPoll } : m));
      })
      .catch(() => {});
  };

  const ch = channels.find(c => c.slug === activeChannel);
  // Модерация сообщений: админ — везде, редактор — только в канале своей специальности
  const canModerate = isAdmin || (isEditor && ch?.specialty && userSpecialties.includes(ch.specialty));
  const statusColor = { connecting: "#f0ad4e", open: "#5cb85c", closed: "#d9534f", readonly: "#9aa5b1" }[wsStatus];
  const statusLabel = { connecting: "подключение…", open: "онлайн", closed: "переподключение…", readonly: "только чтение" }[wsStatus];

  return (
    <section className="forum-shell">
      <h1 className="sr-only">Обсуждения</h1>

      {/* ── Левая панель: список каналов ── */}
      <div className="forum-channels">
        <div className="forum-channels-label" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>Каналы · {specialty?.label}</span>
          {canCreateChannel && (
            <button type="button" onClick={() => setShowCreate(s => !s)}
              title="Создать канал" style={{ background: "none", border: "none", color: "#007c84",
                cursor: "pointer", padding: "0 4px", display: "flex", alignItems: "center" }}>
              {showCreate ? <X size={16} strokeWidth={2.4} /> : <Plus size={16} strokeWidth={2.4} />}
            </button>
          )}
        </div>

        {isLoggedIn && (
          <div style={{ display: "flex", gap: 4, padding: "6px 10px 2px" }}>
            {[{ id: false, label: "Все" }, { id: true, label: "Мои" }].map(opt => (
              <button key={String(opt.id)} type="button" onClick={() => setShowOnlyMine(opt.id)}
                style={{
                  flex: 1, padding: "4px 0", fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
                  border: "none", borderRadius: 6,
                  background: showOnlyMine === opt.id ? "#007c84" : "#f0f2f8",
                  color: showOnlyMine === opt.id ? "#fff" : "#666",
                }}>
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {showCreate && (
          <form onSubmit={createChannel} style={{ padding: "8px 10px", borderBottom: "1.5px solid #dde3f0",
            display: "flex", flexDirection: "column", gap: 6, background: "#e9edf7" }}>
            <input value={newLabel} onChange={e => setNewLabel(e.target.value)}
              placeholder="Название канала" maxLength={80} required
              style={{ padding: "5px 8px", borderRadius: 6, border: "1px solid #c4ceda", fontSize: "0.85rem" }} />
            <input value={newDesc} onChange={e => setNewDesc(e.target.value)}
              placeholder="Описание (необязательно)" maxLength={300}
              style={{ padding: "5px 8px", borderRadius: 6, border: "1px solid #c4ceda", fontSize: "0.85rem" }} />
            {createError && <div style={{ color: "#c0392b", fontSize: "0.78rem" }}>{createError}</div>}
            <button type="submit" disabled={createBusy || !newLabel.trim()}
              style={{ background: "#007c84", color: "#fff", border: "none", borderRadius: 6,
                padding: "5px 10px", fontSize: "0.82rem", cursor: "pointer", fontWeight: 700 }}>
              {createBusy ? "Создание…" : "Создать канал"}
            </button>
          </form>
        )}

        {visibleChannels.length === 0 && showOnlyMine && (
          <div style={{ padding: "16px 14px", fontSize: "0.85rem", color: "#999", textAlign: "center", lineHeight: 1.5 }}>
            Вы ещё не подписаны ни на один канал.<br />
            Нажмите «Все», чтобы посмотреть каналы и отметить ★ нужные.
          </div>
        )}
        {visibleChannels.map(c => (
          <button
            key={c.slug}
            onClick={() => setActiveChannel(c.slug)}
            className={`forum-channel-btn${activeChannel === c.slug ? " active" : ""}`}
          >
            <span className="forum-channel-label">
              {isLoggedIn && (
                <span
                  onClick={e => toggleFollowChannel(c, e)}
                  title={myChannelIds.has(c.id) ? "Отписаться" : "Подписаться"}
                  style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: 32, height: 32, marginLeft: -6, flexShrink: 0, borderRadius: 8,
                    opacity: followBusyId === c.id ? 0.4 : 1, cursor: "pointer",
                  }}
                >
                  <Star size={16} strokeWidth={2}
                    fill={myChannelIds.has(c.id) ? "#f59e0b" : "none"}
                    color={myChannelIds.has(c.id) ? "#f59e0b" : "#bbb"} />
                </span>
              )}
              {c.label}
              {unread[c.slug] && (
                <span style={{
                  width: 8, height: 8, borderRadius: "50%", background: "#e5005b", display: "inline-block",
                  flexShrink: 0
                }} title="Новые сообщения" />
              )}
              {(isAdmin || c.created_by === user?.id) && (
                <span onClick={(e) => { e.stopPropagation(); deleteChannel(c.id); }}
                  title="Удалить канал" style={{ marginLeft: "auto", color: "#c0392b", opacity: 0.55, cursor: "pointer", display: "inline-flex" }}>
                  <X size={13} strokeWidth={2.4} />
                </span>
              )}
            </span>
            <span className="forum-channel-desc">{c.description}</span>
          </button>
        ))}
      </div>

      {/* ── Правая часть: чат ── */}
      <div className="forum-chat-pane">

        {!ch ? (
          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#999" }}>
            {channels.length === 0 ? "В этой специальности пока нет каналов" : "Загрузка…"}
          </div>
        ) : (
        <>
        {/* Заголовок канала */}
        <div className="forum-chat-header" style={{ padding: "11px 20px", borderBottom: "1.5px solid #eaecf4", background: "#f7f9fd",
          display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: "1.04rem", color: "#007c84", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ch.label}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: "0.76rem", color: "#888" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusColor, display: "inline-block", flexShrink: 0 }} />
              {statusLabel}
            </div>
          </div>

          <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
            {isLoggedIn && isPushSupported() && (
              <button
                onClick={togglePush}
                disabled={pushBusy}
                title={pushError || (pushEnabled ? "Отключить push-уведомления" : "Включить push-уведомления о новых сообщениях")}
                style={{
                  background: "none", border: "none", cursor: "pointer", padding: 8, display: "flex",
                  color: pushEnabled ? "#15803d" : "#888",
                }}
              >
                {pushEnabled ? <Bell size={17} strokeWidth={2.2} /> : <BellOff size={17} strokeWidth={2.2} />}
              </button>
            )}
            <button
              onClick={() => { setShowSearch(s => !s); if (showSearch) setSearchQuery(""); }}
              title="Поиск по сообщениям"
              style={{ background: "none", border: "none", color: showSearch ? "#007c84" : "#888", cursor: "pointer", padding: 8, display: "flex" }}
            >
              <Search size={17} strokeWidth={2.2} />
            </button>
          </span>
        </div>
        {showSearch && (
          <div style={{ padding: "8px 20px", background: "#fff", borderBottom: "1.5px solid #eaecf4", display: "flex", alignItems: "center", gap: 8 }}>
            <input
              autoFocus type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              placeholder="Поиск по сообщениям…"
              style={{ flex: 1, maxWidth: 320, borderRadius: 8, border: "1px solid #c4ceda", padding: "6px 12px", fontSize: "0.88rem", outline: "none" }}
            />
            {searchQuery.trim() && (
              <span style={{ fontSize: "0.78rem", color: "#888" }}>
                {searchResults.length > 0 ? `${searchIndex + 1}/${searchResults.length}` : "0"}
              </span>
            )}
            <button disabled={searchResults.length === 0} onClick={() => setSearchIndex(i => (i + 1) % searchResults.length)}
              style={{ background: "none", border: "none", color: "#007c84", cursor: "pointer", padding: 2, display: "flex", opacity: searchResults.length === 0 ? 0.4 : 1 }}>
              <ChevronUp size={16} strokeWidth={2.2} />
            </button>
            <button disabled={searchResults.length === 0} onClick={() => setSearchIndex(i => (i - 1 + searchResults.length) % searchResults.length)}
              style={{ background: "none", border: "none", color: "#007c84", cursor: "pointer", padding: 2, display: "flex", opacity: searchResults.length === 0 ? 0.4 : 1 }}>
              <ChevronDown size={16} strokeWidth={2.2} />
            </button>
          </div>
        )}
        {pushError && (
          <div style={{ padding: "6px 20px", background: "#fdecea", color: "#c0392b", fontSize: "0.8rem" }}>
            {pushError}
          </div>
        )}
        {showOnboarding && (
          <div style={{
            padding: "8px 20px", background: "#e6f6f7", borderBottom: "1.5px solid #dde3f0",
            display: "flex", alignItems: "center", gap: 10, fontSize: "0.82rem", color: "#007c84",
          }}>
            <span style={{ flex: 1 }}>
              ★ — подписка на канал · 🎤 — голосовое (тап начинает и завершает запись) · 🔍 — поиск по переписке
            </span>
            <button onClick={dismissOnboarding} style={{ background: "none", border: "none", color: "#007c84", cursor: "pointer", display: "flex", flexShrink: 0, padding: 2 }}>
              <X size={15} strokeWidth={2.4} />
            </button>
          </div>
        )}

        {pinnedMessages.length > 0 && (
          <div style={{ padding: "8px 20px", background: "#fff1f6", borderBottom: "1.5px solid #ffc2d9", display: "flex", flexDirection: "column", gap: 4 }}>
            {pinnedMessages.map(pm => (
              <div key={pm.id} onClick={() => msgRefs.current[pm.id]?.scrollIntoView({ block: "center", behavior: "smooth" })}
                style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: "0.85rem" }}>
                <Pin size={13} strokeWidth={2.2} style={{ color: "#c4004f", flexShrink: 0 }} />
                <span style={{ color: "#9c0040", fontWeight: 600, flexShrink: 0 }}>{pm.author}:</span>
                <span style={{ color: "#78350f", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {pm.text || (pm.image_url ? "📷 Фото" : pm.audio_url ? "🎤 Голосовое" : pm.file_name || "📎 Файл")}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Лента сообщений */}
        <div ref={chatRef} style={{ flex: 1, overflowY: "auto", padding: "16px 18px",
          display: "flex", flexDirection: "column", gap: 10, background: "#fafbfd" }}>
          {messages.length === 0 && wsStatus !== "connecting" && (
            <div style={{ textAlign: "center", color: "#bbb", marginTop: 40, fontSize: "0.95rem" }}>
              Сообщений пока нет. Начните обсуждение!
            </div>
          )}
          {messages.length === 0 && wsStatus === "connecting" && (
            <div style={{ textAlign: "center", color: "#bbb", marginTop: 40, fontSize: "0.95rem" }}>
              Загрузка истории…
            </div>
          )}
          {messages.map(msg => {
            const isMe = myMsgIds.current.has(msg.id) || (isLoggedIn && msg.author === user?.display_name);
            const ts = msg.created_at ? fmtTime(msg.created_at) : (msg.time || "");
            return (
              <div key={msg.id} ref={el => { if (el) msgRefs.current[msg.id] = el; }} className="chat-msg-row" style={{ display: "flex",
                flexDirection: isMe ? "row-reverse" : "row", alignItems: "flex-end", gap: 8 }}>
                {/* Аватар */}
                <div style={{
                  width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                  background: isMe ? "#007c84" : "#e5005b",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontWeight: 700, fontSize: "0.88rem"
                }}>
                  {(msg.author || "?")[0].toUpperCase()}
                </div>
                {/* Пузырь */}
                <div className="chat-bubble-wrap" style={{ maxWidth: "62%", position: "relative" }}>
                  {!isMe && (
                    <div style={{ fontSize: "0.74rem", color: "#888", marginBottom: 2, marginLeft: 4 }}>
                      {msg.author}
                    </div>
                  )}
                  <div style={{
                    background: isMe ? "#ddeaff" : "#fff",
                    border: isMe ? "1px solid #b3c9f0" : "1px solid #e5e8f0",
                    borderRadius: isMe ? "14px 14px 3px 14px" : "14px 14px 14px 3px",
                    padding: (msg.image_url || msg.audio_url || msg.file_url || msg.poll) ? 6 : "9px 14px", fontSize: "0.97rem", color: "#1a1a2e",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.05)", wordBreak: "break-word"
                  }}>
                    {msg.reply_to_id && (
                      <div
                        onClick={() => msgRefs.current[msg.reply_to_id]?.scrollIntoView({ block: "center", behavior: "smooth" })}
                        style={{
                          borderLeft: "3px solid " + (isMe ? "#007c84" : "#006870"), background: "rgba(0,0,0,0.04)",
                          borderRadius: 6, padding: "4px 8px", marginBottom: 6, cursor: "pointer",
                        }}
                      >
                        <div style={{ fontSize: "0.74rem", fontWeight: 700, color: isMe ? "#007c84" : "#006870" }}>{msg.reply_to_author}</div>
                        <div style={{ fontSize: "0.78rem", color: "#666", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{msg.reply_to_text}</div>
                      </div>
                    )}
                    {msg.image_url && (
                      <a href={msg.image_url} target="_blank" rel="noopener noreferrer">
                        <img src={msg.image_url} alt="" style={{
                          display: "block", maxWidth: "100%", maxHeight: 280,
                          borderRadius: 10, marginBottom: msg.text ? 6 : 0,
                        }} />
                      </a>
                    )}
                    {msg.audio_url && (
                      <VoicePlayer
                        src={msg.audio_url} accent={isMe ? "#007c84" : "#006870"}
                        messageId={msg.id} token={token} transcript={msg.transcript}
                        onTranscribed={t => setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, transcript: t } : m))}
                      />
                    )}
                    {msg.file_url && (
                      <a href={msg.file_url} target="_blank" rel="noopener noreferrer" download
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", borderRadius: 8,
                          background: "rgba(0,0,0,0.03)", textDecoration: "none", color: "inherit", marginBottom: msg.text ? 6 : 0 }}>
                        <FileText size={22} strokeWidth={1.8} style={{ color: isMe ? "#007c84" : "#006870", flexShrink: 0 }} />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: "0.88rem", fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{msg.file_name || "Файл"}</span>
                          <span style={{ display: "block", fontSize: "0.74rem", color: "#888" }}>{fmtFileSize(msg.file_size)}</span>
                        </span>
                        <Download size={16} strokeWidth={2} style={{ color: "#999", flexShrink: 0 }} />
                      </a>
                    )}
                    {msg.poll && (
                      <div style={{ padding: 6 }}>
                        <PollView poll={msg.poll} onVote={voteOnPoll} accent={isMe ? "#007c84" : "#006870"} />
                      </div>
                    )}
                    {msg.text && (
                      <span style={{ padding: msg.image_url ? "0 6px 4px" : 0, display: "block" }}>{highlightText(msg.text)}</span>
                    )}
                  </div>
                  {(() => {
                    const link = extractContentLink(msg.text);
                    return link ? (
                      <div style={{ marginTop: 4 }}>
                        <ChatContentPreview kind={link.kind} id={link.id} />
                      </div>
                    ) : null;
                  })()}
                  {msg.reactions && msg.reactions.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4,
                      justifyContent: isMe ? "flex-end" : "flex-start" }}>
                      {msg.reactions.map(r => (
                        <button key={r.emoji} onClick={() => toggleReaction(msg.id, r.emoji)}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 3,
                            background: r.mine ? "#c4e9eb" : "#f0f2f8",
                            border: r.mine ? "1px solid #a5b4fc" : "1px solid #e5e8f0",
                            borderRadius: 12, padding: "1px 7px", fontSize: "0.82rem", cursor: "pointer",
                          }}>
                          <span>{r.emoji}</span>
                          <span style={{ fontSize: "0.72rem", color: "#666", fontWeight: 600 }}>{r.count}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: "0.71rem", color: "#aab", marginTop: 3,
                    textAlign: isMe ? "right" : "left", paddingLeft: 4, paddingRight: 4,
                    display: "flex", alignItems: "center", gap: 8,
                    justifyContent: isMe ? "flex-end" : "flex-start" }}>
                    {ts}
                    {isMe && (
                      Object.entries(readReceipts).some(([author, upToId]) => author !== msg.author && upToId >= msg.id)
                        ? <CheckCheck size={13} strokeWidth={2.4} style={{ color: "#007c84" }} />
                        : <Check size={13} strokeWidth={2.4} style={{ color: "#aab" }} />
                    )}
                    <button
                      onClick={() => setReplyingTo({ id: msg.id, author: msg.author, preview: msg.text || (msg.image_url ? "📷 Фото" : msg.audio_url ? "🎤 Голосовое" : msg.file_name || "📎 Файл") })}
                      title="Ответить"
                      style={{ background: "none", border: "none", color: "#999", cursor: "pointer",
                        padding: 0, display: "inline-flex", alignItems: "center", opacity: 0.7 }}
                    >
                      <Reply size={12} strokeWidth={2.4} />
                    </button>
                    <span style={{ position: "relative", display: "inline-flex" }}>
                      <button
                        onClick={e => { e.stopPropagation(); setReactingToId(prev => prev === msg.id ? null : msg.id); }}
                        title="Реакция"
                        style={{ background: "none", border: "none", color: "#999", cursor: "pointer",
                          padding: 0, display: "inline-flex", alignItems: "center", opacity: 0.7 }}
                      >
                        <SmilePlus size={12} strokeWidth={2.4} />
                      </button>
                      {reactingToId === msg.id && (
                        <div style={{
                          position: "absolute", bottom: "100%", marginBottom: 6,
                          [isMe ? "right" : "left"]: 0,
                          background: "#fff", border: "1px solid #e0e3ee", borderRadius: 20,
                          boxShadow: "0 4px 16px rgba(0,0,0,0.14)", padding: "4px 6px",
                          display: "flex", gap: 2, zIndex: 15, whiteSpace: "nowrap",
                        }}>
                          {QUICK_REACTIONS.map(e => (
                            <button key={e} onClick={() => toggleReaction(msg.id, e)}
                              style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.1rem", padding: "2px 4px" }}>
                              {e}
                            </button>
                          ))}
                        </div>
                      )}
                    </span>
                    {canModerate && (
                      <>
                        <button
                          onClick={() => togglePin(msg)}
                          title={msg.is_pinned ? "Открепить" : "Закрепить"}
                          style={{ background: "none", border: "none", color: msg.is_pinned ? "#c4004f" : "#999",
                            cursor: "pointer", padding: 0, opacity: msg.is_pinned ? 1 : 0.6,
                            display: "inline-flex", alignItems: "center" }}
                        >
                          {msg.is_pinned ? <PinOff size={12} strokeWidth={2.4} /> : <Pin size={12} strokeWidth={2.4} />}
                        </button>
                        <button
                          onClick={() => deleteMessage(msg.id)}
                          title="Удалить сообщение"
                          style={{ background: "none", border: "none", color: "#c0392b",
                            cursor: "pointer", fontSize: "0.78rem", padding: 0, opacity: 0.6,
                            display: "inline-flex", alignItems: "center", gap: 3 }}
                        >
                          <X size={11} strokeWidth={2.4} /> удалить
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Поле ввода / Auth gate */}
        {isLoggedIn ? (
          <div style={{ borderTop: "1.5px solid #eaecf4", background: "#f7f9fd" }}>
            {replyingTo && (
              <div style={{
                padding: "7px 14px", background: "#eef0fa", display: "flex", alignItems: "center", gap: 8,
                borderBottom: "1px solid #dde1f2",
              }}>
                <div style={{ width: 3, alignSelf: "stretch", background: "#007c84", borderRadius: 2, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: "0.76rem", fontWeight: 700, color: "#007c84" }}>Ответ {replyingTo.author}</div>
                  <div style={{ fontSize: "0.8rem", color: "#666", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{replyingTo.preview}</div>
                </div>
                <button onClick={() => setReplyingTo(null)} style={{ background: "none", border: "none", color: "#888", cursor: "pointer", display: "flex", padding: 4 }}>
                  <X size={16} strokeWidth={2.4} />
                </button>
              </div>
            )}
            {typingUsers.length > 0 && (
              <div style={{
                padding: "6px 14px 0", fontSize: "0.8rem", color: "#007c84", fontStyle: "italic",
                display: "flex", alignItems: "center", gap: 5,
              }}>
                <span style={{ display: "inline-flex", gap: 2 }}>
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                  <span className="typing-dot" />
                </span>
                {typingUsers.length === 1 ? `${typingUsers[0]} печатает…` : `${typingUsers.join(", ")} печатают…`}
              </div>
            )}
            {uploadError && (
              <div style={{ padding: "6px 14px", color: "#c0392b", fontSize: "0.78rem" }}>{uploadError}</div>
            )}
            {(pendingImage || uploadingImage) && (
              <div style={{ padding: "8px 14px 0", display: "flex", alignItems: "center", gap: 8 }}>
                {uploadingImage ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.85rem", color: "#888" }}>
                    <Loader2 size={14} className="spin" strokeWidth={2.2} /> Загрузка фото…
                  </span>
                ) : (
                  <div style={{ position: "relative", display: "inline-block" }}>
                    <img src={pendingImage.previewUrl} alt="" style={{ height: 64, borderRadius: 8, border: "1px solid #c4ceda" }} />
                    <button type="button" onClick={() => setPendingImage(null)}
                      style={{ position: "absolute", top: -6, right: -6, background: "#c0392b", color: "#fff",
                        border: "2px solid #f7f9fd", borderRadius: "50%", width: 20, height: 20,
                        display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", padding: 0 }}>
                      <X size={11} strokeWidth={3} />
                    </button>
                  </div>
                )}
              </div>
            )}
            {(pendingFile || uploadingFile) && (
              <div style={{ padding: "8px 14px 0", display: "flex", alignItems: "center", gap: 8 }}>
                {uploadingFile ? (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.85rem", color: "#888" }}>
                    <Loader2 size={14} className="spin" strokeWidth={2.2} /> Загрузка файла…
                  </span>
                ) : (
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#f0f2f8", borderRadius: 8, padding: "6px 10px" }}>
                    <FileText size={16} strokeWidth={2} style={{ color: "#007c84" }} />
                    <span style={{ fontSize: "0.85rem", maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{pendingFile.name}</span>
                    <span style={{ fontSize: "0.75rem", color: "#888" }}>{fmtFileSize(pendingFile.size)}</span>
                    <button type="button" onClick={() => setPendingFile(null)}
                      style={{ background: "none", border: "none", color: "#999", cursor: "pointer", display: "flex", padding: 0 }}>
                      <X size={14} strokeWidth={2.4} />
                    </button>
                  </div>
                )}
              </div>
            )}
            {isRecording ? (
              <div style={{ display: "flex", gap: 10, padding: "10px 14px", alignItems: "center" }}>
                <button type="button" onClick={() => stopRecording(true)}
                  style={{ background: "none", border: "none", color: "#c0392b", cursor: "pointer", display: "flex", padding: 6 }}>
                  <Trash2 size={19} strokeWidth={2.2} />
                </button>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: "#e11d48", animation: "typingBounce 1s infinite" }} />
                <span style={{ flex: 1, color: "#333", fontSize: "0.95rem" }}>
                  Запись… {Math.floor(recordingSeconds / 60)}:{(recordingSeconds % 60).toString().padStart(2, "0")}
                  <span style={{ display: "block", fontSize: "0.78rem", color: "#888", fontWeight: 400 }}>Нажмите ✓, чтобы отправить, 🗑 — отменить</span>
                </span>
                <button type="button" onClick={() => stopRecording(false)}
                  style={{ background: "#007c84", color: "#fff", border: "none", borderRadius: "50%",
                    width: 38, height: 38, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                  <Send size={16} strokeWidth={2.2} />
                </button>
              </div>
            ) : (
            <form onSubmit={send} style={{ display: "flex", gap: 6, padding: "10px 14px", alignItems: "center", position: "relative" }}>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp"
                style={{ display: "none" }} onChange={handleFileSelect} />
              <button type="button" onClick={() => fileInputRef.current?.click()}
                title="Прикрепить фото"
                style={{ background: "none", border: "none", color: "#888", cursor: "pointer",
                  padding: 6, display: "flex", alignItems: "center" }}>
                <Paperclip size={19} strokeWidth={2} />
              </button>
              <input ref={fileDocInputRef} type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf,.zip"
                style={{ display: "none" }} onChange={handleFileDocSelect} />
              <button type="button" onClick={() => fileDocInputRef.current?.click()}
                title="Прикрепить документ"
                style={{ background: "none", border: "none", color: "#888", cursor: "pointer",
                  padding: 6, display: "flex", alignItems: "center" }}>
                <FileText size={19} strokeWidth={2} />
              </button>
              <button type="button" onClick={() => { if (!isLoggedIn) { onLoginRequest?.(); return; } setShowPollComposer(true); }}
                title="Создать опрос"
                style={{ background: "none", border: "none", color: "#888", cursor: "pointer",
                  padding: 6, display: "flex", alignItems: "center" }}>
                <BarChart3 size={19} strokeWidth={2} />
              </button>
              <button type="button" onClick={() => setShowEmoji(s => !s)}
                title="Эмодзи"
                style={{ background: "none", border: "none", color: showEmoji ? "#007c84" : "#888", cursor: "pointer",
                  padding: 6, display: "flex", alignItems: "center" }}>
                <Smile size={19} strokeWidth={2} />
              </button>
              {showEmoji && <EmojiPicker onSelect={insertEmoji} onClose={() => setShowEmoji(false)} />}
              <span style={{ position: "relative", flex: 1 }}>
                {mentionSuggestions.length > 0 && (
                  <div style={{
                    position: "absolute", bottom: "100%", left: 0, marginBottom: 6,
                    background: "#fff", border: "1px solid #e0e3ee", borderRadius: 10,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)", minWidth: 200, maxHeight: 200,
                    overflowY: "auto", zIndex: 20,
                  }}>
                    {mentionSuggestions.map(u => (
                      <button key={u.id} type="button" onClick={() => selectMention(u)}
                        style={{ display: "flex", flexDirection: "column", width: "100%", textAlign: "left",
                          background: "none", border: "none", cursor: "pointer", padding: "7px 12px" }}
                        onMouseEnter={e => e.currentTarget.style.background = "#f0f2f8"}
                        onMouseLeave={e => e.currentTarget.style.background = "none"}>
                        <span style={{ fontWeight: 600, fontSize: "0.88rem", color: "#007c84" }}>@{u.username}</span>
                        <span style={{ fontSize: "0.76rem", color: "#888" }}>{u.display_name}</span>
                      </button>
                    ))}
                  </div>
                )}
                <input
                  ref={textInputRef}
                  type="text" value={text} onChange={handleTextChange}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { send(e); } }}
                  placeholder="Сообщение… (Enter — отправить, @ — упомянуть)"
                  maxLength={MAX_LEN}
                  style={{ width: "100%", borderRadius: 8, border: "1px solid #c4ceda",
                    padding: "8px 12px", fontSize: "0.97rem", background: "#fff", outline: "none", boxSizing: "border-box" }}
                />
              </span>
              {text.length > MAX_LEN - 100 && (
                <span style={{ fontSize: "0.76rem", color: text.length >= MAX_LEN ? "#c0392b" : "#888" }}>
                  {text.length}/{MAX_LEN}
                </span>
              )}
              {(text.trim() || pendingImage || pendingFile) ? (
                <button
                  type="submit"
                  style={{ background: "#007c84", color: "#fff",
                    borderRadius: 8, padding: "8px 20px", fontWeight: 700, fontSize: "0.97rem",
                    border: "none", cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 7,
                    transition: "background 0.15s", minWidth: 90 }}
                >
                  Отправить <Send size={14} strokeWidth={2.2} />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={startRecording}
                  disabled={uploadingAudio}
                  title="Голосовое сообщение"
                  style={{ background: "#007c84", color: "#fff",
                    borderRadius: "50%", width: 38, height: 38, padding: 0,
                    border: "none", cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    opacity: uploadingAudio ? 0.6 : 1 }}
                >
                  {uploadingAudio ? <Loader2 size={17} className="spin" strokeWidth={2.2} /> : <Mic size={17} strokeWidth={2.2} />}
                </button>
              )}
            </form>
            )}
          </div>
        ) : (
          <div style={{ padding: "14px 20px", borderTop: "1.5px solid #eaecf4",
            background: "#f7f9fd", textAlign: "center", fontSize: "0.93rem", color: "#555" }}>
            Вы можете читать обсуждение свободно. Чтобы написать —{" "}
            <button onClick={onLoginRequest}
              style={{ background: "none", color: "#007c84", textDecoration: "underline",
                border: "none", cursor: "pointer", fontWeight: 700, padding: 0, margin: 0,
                fontSize: "0.93rem" }}>
              войдите
            </button>{" или "}
            <button onClick={onLoginRequest}
              style={{ background: "none", color: "#007c84", textDecoration: "underline",
                border: "none", cursor: "pointer", fontWeight: 700, padding: 0, margin: 0,
                fontSize: "0.93rem" }}>
              зарегистрируйтесь
            </button>
          </div>
        )}
        </>
        )}
      </div>
      {showPollComposer && (
        <PollComposer onSubmit={createPoll} onClose={() => setShowPollComposer(false)} />
      )}
    </section>
  );
}
